import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  CheckRounded,
  NotificationsActiveOutlined,
  StopCircleOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import { formatTime } from "../../../Helpers/dateHelpers";
import {
  DocumentCraftingRun,
  DocumentCraftingRunKind,
  DocumentCraftingRunStatus,
  DocumentCraftingRunStep,
  MAX_FIX_ROUNDS,
} from "../../../Models/documentCrafting";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import { Panel, RunStatusChip, StatusChip } from "../Common/DocumentCraftingUi";

/** Step keys per run kind (CONTRACTS §7.2), used when the run lists none. */
export const DEFAULT_STEPS: Record<DocumentCraftingRunKind, DocumentCraftingRunStep[]> = {
  [DocumentCraftingRunKind.CREATE]: [
    "reading_sources",
    "analysing",
    "writing_structure",
    "writing_content",
    "laying_out",
    "checking",
    "fixing",
    "rendering_preview",
    "saving",
  ],
  [DocumentCraftingRunKind.REVISE]: [
    "reading_sources",
    "analysing",
    "writing_structure",
    "writing_content",
    "laying_out",
    "checking",
    "fixing",
    "rendering_preview",
    "saving",
  ],
  [DocumentCraftingRunKind.REFINE]: [
    "analysing",
    "writing_content",
    "laying_out",
    "checking",
    "fixing",
    "rendering_preview",
    "saving",
  ],
  [DocumentCraftingRunKind.UPLOAD]: ["converting", "checking", "ingesting", "rendering_preview", "saving"],
  [DocumentCraftingRunKind.TRANSLATE]: ["translating", "laying_out", "rendering_preview"],
};

type StepState = "done" | "active" | "pending";

interface Props {
  run: DocumentCraftingRun;
  elapsedSeconds: number;
  onCancel?: () => void;
  cancelPending?: boolean;
}

const StepRow: React.FC<{
  label: string;
  state: StepState;
  detail?: string;
  last: boolean;
}> = ({ label, state, detail, last }) => {
  const { accent, onAccent } = useDashboardColors();
  return (
    <Box component="li" sx={{ display: "grid", gridTemplateColumns: "28px minmax(0, 1fr)", gap: 1.5 }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: state === "done" ? accent : color.white,
            border: `2px solid ${state === "pending" ? color.gray200 : accent}`,
            color: onAccent,
          }}
        >
          {state === "done" && <CheckRounded sx={{ fontSize: 16 }} />}
          {state === "active" && <CircularProgress size={14} thickness={6} sx={{ color: accent }} />}
        </Box>
        {!last && (
          <Box
            sx={{
              flex: 1,
              width: 2,
              minHeight: 14,
              my: 0.25,
              backgroundColor: state === "done" ? accent : color.gray200,
            }}
          />
        )}
      </Box>
      <Box sx={{ pb: last ? 0 : 1.5, pt: "2px" }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: state === "active" ? 700 : 500,
            color: state === "pending" ? color.gray500 : color.gray900,
          }}
        >
          {label}
        </Typography>
        {detail && <Typography sx={{ fontSize: 12, color: color.gray500 }}>{detail}</Typography>}
      </Box>
    </Box>
  );
};

/**
 * Progress of a working run: the steps of its kind (done / working / to do),
 * the progress bar, the elapsed time, the fix round and Cancel while the run
 * can still be stopped.
 */
const RunProgress: React.FC<Props> = ({ run, elapsedSeconds, onCancel, cancelPending }) => {
  const { t } = useTranslation("documentCrafting");
  const steps = run.steps?.length ? run.steps : DEFAULT_STEPS[run.kind] ?? [];
  const queued = run.status === DocumentCraftingRunStatus.QUEUED;
  const current = run.step ? steps.indexOf(run.step) : -1;
  const progress = Math.max(0, Math.min(100, run.progress ?? 0));
  const maxRounds = run.max_fix_rounds || MAX_FIX_ROUNDS;
  const fixRound =
    run.fix_round > 0 ? t("run.progress.fixRound", { round: run.fix_round, max: maxRounds }) : undefined;

  const stateOf = (index: number): StepState => {
    if (queued || current < 0) return "pending";
    if (index < current) return "done";
    return index === current ? "active" : "pending";
  };
  const stepLabel = (step: string) => t(`run.steps.${step}`, { defaultValue: step });

  return (
    <Panel
      title={t("run.progress.title")}
      action={<RunStatusChip status={run.status} />}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ flex: 1, height: 8 }}
          aria-label={t("run.progress.percent", { percent: progress })}
        />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: color.gray700, minWidth: 40, textAlign: "right" }}>
          {t("run.progress.percent", { percent: progress })}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2.5 }}>
        <Typography sx={{ fontSize: 13, color: color.gray500 }}>
          {queued
            ? t("run.progress.queued")
            : run.step && current < 0
              ? t("run.progress.current", { step: stepLabel(run.step) })
              : t("run.progress.working")}
        </Typography>
        <Typography sx={{ fontSize: 13, color: color.gray500, fontVariantNumeric: "tabular-nums" }}>
          {t("run.progress.elapsed", { time: formatTime(elapsedSeconds) })}
        </Typography>
      </Box>

      <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none" }}>
        {steps.map((step, index) => (
          <StepRow
            key={step}
            label={stepLabel(step)}
            state={stateOf(index)}
            detail={
              (step === "fixing" || step === "checking") && stateOf(index) === "active" ? fixRound : undefined
            }
            last={index === steps.length - 1}
          />
        ))}
      </Box>

      <Box
        sx={{
          mt: 2.5,
          pt: 2,
          borderTop: `1px solid ${color.gray100}`,
          display: "flex",
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", color: color.gray500 }}>
          <NotificationsActiveOutlined sx={{ fontSize: 18, mt: "1px" }} />
          <Typography sx={{ fontSize: 13, color: color.gray500 }}>{t("run.progress.leaveHint")}</Typography>
        </Box>
        {run.cancel_requested ? (
          <StatusChip label={t("run.progress.cancelling")} tone="gray" />
        ) : (
          run.can_cancel &&
          onCancel && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<StopCircleOutlined />}
              onClick={onCancel}
              disabled={cancelPending}
              sx={{ flexShrink: 0 }}
            >
              {t("run.progress.cancel")}
            </Button>
          )
        )}
      </Box>
    </Panel>
  );
};

export default RunProgress;
